import {
    AutoProduct,
    BundleProduct,
    CalendarIcon,
    Column,
    Container,
    cssVar,
    Grid,
    getFormattedCurrency,
    Header,
    HomeProduct,
    HourglassIcon,
    Label,
    Link,
    Row,
    SecondaryButton,
    useAlignments,
    useSizes,
} from 'metrix-common3';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import Helper from '../../../../helpers';

const PolicyFactsContainer = styled(Container)`
    border: 1px solid ${cssVar.colorStrokeLight};
    border-radius: 4px;
`;

const HeaderCell = styled(Container)`
    background: ${cssVar.colorStrokeVeryLight};
    padding: 10px;
`;

const BodyCell = styled(Container)`
    ${({ firstRow }) => firstRow && `border-top: 1px solid ${cssVar.colorStrokeVeryLight};`}
    border-bottom: 1px solid ${cssVar.colorStrokeVeryLight};
    ${({ firstCell }) => firstCell && `border-left: 1px solid ${cssVar.colorStrokeVeryLight};`}
    border-right: 1px solid ${cssVar.colorStrokeVeryLight};
    padding: 5px;
`;

const FooterCell = styled(Container)`
    background: ${cssVar.colorStrokeVeryLight};
    ${({ firstRow }) => firstRow && `border-top: 1px solid ${cssVar.colorStrokeVeryLight};`}
    border-bottom: 1px solid ${cssVar.colorStrokeVeryLight};
    ${({ firstCell }) => firstCell && `border-left: 1px solid ${cssVar.colorStrokeVeryLight};`}
    border-right: 1px solid ${cssVar.colorStrokeVeryLight};
    ${({ hasTopBorder }) => hasTopBorder && `border-top: 2px solid ${cssVar.colorStrokeDark};`}
    padding: 5px;
`;

const ProfileSummaryAction = (props) => {
    const intl = useIntl();
    const sizes = useSizes();
    const alignments = useAlignments();

    const policyStatus = props.policySummary.status;
    const policyType = props.policySummary.type;
    const statusLabel = intl.formatMessage({ id: `Profile_Summary_Action.${policyStatus}` });
    const typeLabel = policyType ? Helper.getDescription(intl, policyType, 'policyTypeDescription') : '';
    const headerText = typeLabel ? `${typeLabel} - ${statusLabel}` : statusLabel;
    const insurerSelected =
        policyStatus === 'quoteAccepted' ||
        policyStatus === 'quoteIssued' ||
        policyStatus === 'bindRequested' ||
        policyStatus === 'bound' ||
        policyStatus === 'issued';

    const insurer =
        insurerSelected && props.policySummary.participants?.insurer?.commercialName?.name
            ? props.policySummary.participants.insurer.commercialName.name
            : '';

    const currency = 'CAD';
    const culture = props.context.session.culture;

    const premiumAmount = props.policySummary.estimatedAnnualPremium.value;
    const taxesAmount = props.policySummary.estimatedTaxes.value;
    const feesAmount = props.policySummary.estimatedFees.value;
    const surchargesAmount = props.policySummary.estimatedSurcharges?.value || 0;
    const discountsAmount = props.policySummary.estimatedDiscounts?.value || 0;

    // Calculate subtotal and total
    // Discount is already in Negative
    const subTotalAmount = premiumAmount + surchargesAmount + discountsAmount + feesAmount;
    const totalAmount = subTotalAmount + taxesAmount;

    const brokerage = Helper.GetBrokerageName(
        props?.policy?.participants?.brokerCompany?.company?.commercialName?.name,
    );

    const assignedTo = Helper.GetBrokerName(
        props?.policy?.participants?.broker?.party?.name?.firstName,
        props?.policy?.participants?.broker?.party?.name?.lastName,
    );

    /*if (props.policySummary.risks) {
        for (var i = 0; i < props.policySummary.risks.length; i++) {
            let risk = props.policySummary.risks[i];
            for (var j = 0; j < risk.coverages.length; j++) {
                let coverage = risk.coverages[0];

            }
        }
    }*/

    return (
        <React.Fragment>
            {props.context?.session?.userType === 'broker' && props.policySummary.role === 'master' && (
                <PolicyFactsContainer top={sizes.sm} bottom={sizes.sm}>
                    <Grid>
                        <Row>
                            <Column>
                                <Header
                                    top={sizes.xs}
                                    size={sizes.lg}
                                    text={intl.formatMessage({ id: 'Profile_Summary_Action.package_policy' })}
                                />
                            </Column>
                        </Row>
                        <Row>
                            <Column>
                                <Label text={intl.formatMessage({ id: 'Profile_Summary_Action.update_underlier' })} />
                            </Column>
                        </Row>
                        {props.policySummary?.relatedPolicyTransactions?.length > 0 &&
                            props.policySummary.relatedPolicyTransactions.map((relatedPolicyTransaction, i) => {
                                return (
                                    // biome-ignore lint/suspicious/noArrayIndexKey: no other way
                                    <Row key={i}>
                                        <Column xs={0} md={3} />
                                        <Column xs={12} md={6}>
                                            <Container
                                                inline
                                                horizontalAlignment={alignments.left}
                                                verticalAlignment={alignments.center}
                                            >
                                                <Grid>
                                                    <Row>
                                                        <Column xs={2}>
                                                            <Container
                                                                fullSize
                                                                horizontalAlignment={alignments.left}
                                                                verticalAlignment={alignments.center}
                                                            >
                                                                {relatedPolicyTransaction.product === 'auto' ? (
                                                                    <AutoProduct color={cssVar.colorPrimary} />
                                                                ) : (
                                                                    <HomeProduct color={cssVar.colorPrimary} />
                                                                )}
                                                            </Container>
                                                        </Column>
                                                        <Column xs={5}>
                                                            <Container
                                                                fullSize
                                                                horizontalAlignment={alignments.left}
                                                                verticalAlignment={alignments.center}
                                                            >
                                                                <Label
                                                                    bold
                                                                    left={sizes.xs}
                                                                    textAlign={alignments.left}
                                                                    text={intl.formatMessage({
                                                                        id:
                                                                            relatedPolicyTransaction.product === 'auto'
                                                                                ? 'Profile_Summary_Action.auto'
                                                                                : 'Profile_Summary_Action.home',
                                                                    })}
                                                                />
                                                            </Container>
                                                        </Column>
                                                        <Column xs={5}>
                                                            <Container
                                                                horizontalAlignment={alignments.right}
                                                                verticalAlignment={alignments.center}
                                                            >
                                                                <SecondaryButton
                                                                    text={intl.formatMessage({
                                                                        id: 'Profile_Summary_Action.summary',
                                                                    })}
                                                                    clickEvent={() => {
                                                                        window.location.href = `${props.context.configuration.policyWeb}/profile?entity=${relatedPolicyTransaction.entity}`;
                                                                    }}
                                                                />
                                                            </Container>
                                                        </Column>
                                                    </Row>
                                                </Grid>
                                            </Container>
                                        </Column>
                                        <Column xs={0} md={3} />
                                    </Row>
                                );
                            })}
                    </Grid>
                </PolicyFactsContainer>
            )}
            {props.context?.session?.userType === 'broker' && props.policySummary.role === 'underlier' && (
                <PolicyFactsContainer top={sizes.sm} bottom={sizes.sm}>
                    <Grid>
                        <Row>
                            <Column>
                                <Header
                                    top={sizes.xs}
                                    size={sizes.lg}
                                    text={intl.formatMessage({ id: 'Profile_Summary_Action.part_of_package' })}
                                />
                            </Column>
                        </Row>
                        <Row>
                            <Column>
                                <Label
                                    top={sizes.sm}
                                    text={intl.formatMessage({ id: 'Profile_Summary_Action.renew_master' })}
                                />
                            </Column>
                        </Row>
                        {props.policySummary?.relatedPolicyTransactions?.length > 0 &&
                            props.policySummary.relatedPolicyTransactions.map((relatedPolicyTransaction, i) => {
                                return (
                                    // biome-ignore lint/suspicious/noArrayIndexKey: no other way
                                    <Row key={i}>
                                        <Column xs={0} md={3} />
                                        <Column xs={12} md={6}>
                                            <Container
                                                inline
                                                horizontalAlignment={alignments.left}
                                                verticalAlignment={alignments.center}
                                            >
                                                <Grid>
                                                    <Row>
                                                        <Column xs={2}>
                                                            <Container
                                                                fullSize
                                                                horizontalAlignment={alignments.left}
                                                                verticalAlignment={alignments.center}
                                                            >
                                                                <BundleProduct color={cssVar.colorPrimary} />
                                                            </Container>
                                                        </Column>
                                                        <Column xs={5}>
                                                            <Container
                                                                fullSize
                                                                horizontalAlignment={alignments.left}
                                                                verticalAlignment={alignments.center}
                                                            >
                                                                <Label
                                                                    bold
                                                                    left={sizes.xs}
                                                                    textAlign={alignments.left}
                                                                    text={
                                                                        relatedPolicyTransaction.product ===
                                                                            'ppackage' &&
                                                                        intl.formatMessage({
                                                                            id: 'Profile_Summary_Action.home_and_auto',
                                                                        })
                                                                    }
                                                                />
                                                            </Container>
                                                        </Column>
                                                        <Column xs={5}>
                                                            <Container
                                                                horizontalAlignment={alignments.right}
                                                                verticalAlignment={alignments.center}
                                                            >
                                                                <SecondaryButton
                                                                    text={intl.formatMessage({
                                                                        id: 'Profile_Summary_Action.summary',
                                                                    })}
                                                                    clickEvent={() => {
                                                                        window.location = `${props.context.configuration.policyWeb}/profile?entity=${relatedPolicyTransaction.entity}`;
                                                                    }}
                                                                />
                                                            </Container>
                                                        </Column>
                                                    </Row>
                                                </Grid>
                                            </Container>
                                        </Column>
                                        <Column xs={0} md={3} />
                                    </Row>
                                );
                            })}
                    </Grid>
                </PolicyFactsContainer>
            )}
            <PolicyFactsContainer top={sizes.sm} bottom={sizes.sm}>
                <Grid>
                    <Row>
                        <Column>
                            <Header top={sizes.xs} size={sizes.lg} text={headerText} />
                        </Column>
                    </Row>
                    {policyStatus === 'lost' && props.policy?.quote?.lostReason?.value && (
                        <Row>
                            <Column>
                                <Header
                                    top={sizes.xs}
                                    size={sizes.xs}
                                    text={`${props.policy.quote.lostReason.value}`}
                                />
                            </Column>
                        </Row>
                    )}
                    <Row>
                        <Column xs={6}>
                            <Container
                                inline
                                horizontalAlignment={alignments.left}
                                verticalAlignment={alignments.center}
                            >
                                <Label
                                    bold
                                    left={sizes.xs}
                                    textAlign={alignments.left}
                                    text={
                                        <FormattedMessage
                                            id={'Profile_Summary_Action.brokerage'}
                                            values={{ text: brokerage }}
                                        />
                                    }
                                />
                            </Container>
                        </Column>
                        <Column xs={6}>
                            <Container
                                inline
                                horizontalAlignment={alignments.left}
                                verticalAlignment={alignments.center}
                            >
                                <Label
                                    bold
                                    left={sizes.xs}
                                    textAlign={alignments.left}
                                    text={
                                        <FormattedMessage
                                            id={'Profile_Summary_Action.assignedTo'}
                                            values={{ text: assignedTo }}
                                        />
                                    }
                                />
                            </Container>
                        </Column>
                    </Row>
                    <Row>
                        <Column xs={6}>
                            <Container
                                inline
                                horizontalAlignment={alignments.left}
                                verticalAlignment={alignments.center}
                            >
                                <CalendarIcon primary={cssVar.colorTertiaryText} />
                                <Label
                                    bold
                                    left={sizes.xs}
                                    textAlign={alignments.left}
                                    text={
                                        <FormattedMessage
                                            id={'Profile_Summary_Action.policyStarts'}
                                            values={{ date: props.policySummary.formattedEffectiveDate }}
                                        />
                                    }
                                />
                            </Container>
                        </Column>
                        <Column xs={6}>
                            <Container
                                inline
                                horizontalAlignment={alignments.left}
                                verticalAlignment={alignments.center}
                            >
                                <HourglassIcon primary={cssVar.colorTertiaryText} />
                                {policyStatus === 'quoted' || policyStatus === 'quoteIssued' ? (
                                    <Label
                                        bold
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={
                                            <FormattedMessage
                                                id={'Profile_Summary_Action.quoteExpires'}
                                                values={{ date: props.policySummary.formattedExpiryDate }}
                                            />
                                        }
                                    />
                                ) : (
                                    <Label
                                        bold
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={
                                            <FormattedMessage
                                                id={'Profile_Summary_Action.policyEnds'}
                                                values={{ date: props.policySummary.formattedExpiryDate }}
                                            />
                                        }
                                    />
                                )}
                            </Container>
                        </Column>
                    </Row>
                    <Row>
                        <Column xs={6}>
                            <Container
                                inline
                                horizontalAlignment={alignments.left}
                                verticalAlignment={alignments.center}
                            >
                                {props.policySummary.status === 'issued' ? (
                                    <Label
                                        bold
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={
                                            <FormattedMessage
                                                id={'Profile_Summary_Action.policyNumber'}
                                                values={{ number: props.policySummary.number }}
                                            />
                                        }
                                    />
                                ) : (
                                    <Label
                                        bold
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={
                                            <FormattedMessage
                                                id={'Profile_Summary_Action.binderNumber'}
                                                values={{ number: props.policySummary.submissionReference }}
                                            />
                                        }
                                    />
                                )}
                            </Container>
                        </Column>
                        {insurerSelected && (
                            <Column xs={6}>
                                <Container
                                    inline
                                    horizontalAlignment={alignments.left}
                                    verticalAlignment={alignments.center}
                                >
                                    <Label
                                        bold
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={
                                            <FormattedMessage
                                                id={'Profile_Summary_Action.selectedInsurer'}
                                                values={{ insurer: insurer }}
                                            />
                                        }
                                    />
                                </Container>
                            </Column>
                        )}
                    </Row>
                    {policyType === 'endorsement' && props.policySummary.status !== 'issued' && (
                        <Row noguttertop>
                            <Column xs={6}>
                                <Container
                                    inline
                                    horizontalAlignment={alignments.left}
                                    verticalAlignment={alignments.center}
                                >
                                    <Label
                                        bold
                                        bottom={sizes.xs}
                                        left={sizes.xs}
                                        textAlign={alignments.left}
                                        text={
                                            <FormattedMessage
                                                id={'Profile_Summary_Action.policyNumber'}
                                                values={{ number: props.policySummary.number }}
                                            />
                                        }
                                    />
                                </Container>
                            </Column>
                        </Row>
                    )}
                    {props.context.session.userType === 'client' && (
                        <Row>
                            <Column>
                                <Label
                                    bottom={sizes.xs}
                                    textAlign={alignments.center}
                                    text={
                                        <FormattedMessage
                                            id={'Profile_Summary_Action.pleaseContact'}
                                            values={{
                                                contact: (
                                                    <Link
                                                        inline
                                                        text={
                                                            <FormattedMessage id={'Profile_Summary_Action.contact'} />
                                                        }
                                                        clickEvent={() =>
                                                            window.scrollTo({
                                                                top: 2000,
                                                                left: 0,
                                                                behavior: 'smooth',
                                                            })
                                                        }
                                                    />
                                                ),
                                            }}
                                        />
                                    }
                                />
                            </Column>
                        </Row>
                    )}
                </Grid>
                {(props.policySummary.status === 'quoteAccepted' ||
                    props.policySummary.status === 'quoteIssued' ||
                    props.policySummary.status === 'bindRequested' ||
                    props.policySummary.status === 'bound' ||
                    props.policySummary.status === 'issued') && (
                    <Container left={sizes.xs} right={sizes.xs} bottom={sizes.xs}>
                        <Grid>
                            <Row nogutter>
                                <Column xs={12} nogutter>
                                    <HeaderCell fullSize>
                                        <Label
                                            bold
                                            textAlign={alignments.center}
                                            text={<FormattedMessage id={'Profile_Summary_Action.summary_header'} />}
                                        />
                                    </HeaderCell>
                                </Column>
                            </Row>
                            {props.policySummary.quoteReference && (
                                <Row nogutter>
                                    <Column xs={8} nogutter>
                                        <BodyCell firstRow firstCell fullSize>
                                            <Label
                                                textAlign={alignments.left}
                                                text={
                                                    <FormattedMessage id={'Profile_Summary_Action.quote_reference'} />
                                                }
                                            />
                                        </BodyCell>
                                    </Column>
                                    <Column xs={4} nogutter>
                                        <BodyCell firstRow fullSize>
                                            <Link
                                                textAlign={alignments.right}
                                                text={props.policySummary.quoteReference}
                                                href={props.policySummary.portalUrl}
                                                newTab
                                            />
                                        </BodyCell>
                                    </Column>
                                </Row>
                            )}
                            <Row nogutter>
                                <Column xs={8} nogutter>
                                    <BodyCell firstRow={!props.policySummary.quoteReference} firstCell fullSize>
                                        <Label
                                            textAlign={alignments.left}
                                            text={<FormattedMessage id={'Profile_Summary_Action.premium'} />}
                                        />
                                    </BodyCell>
                                </Column>
                                <Column xs={4} nogutter>
                                    <BodyCell firstRow={!props.policySummary.quoteReference} fullSize>
                                        <Label
                                            textAlign={alignments.right}
                                            text={props.policySummary.formattedEstimatedAnnualPremium}
                                        />
                                    </BodyCell>
                                </Column>
                            </Row>
                            <Row nogutter>
                                <Column xs={8} nogutter>
                                    <BodyCell firstCell fullSize>
                                        <Label
                                            textAlign={alignments.left}
                                            text={<FormattedMessage id={'Profile_Summary_Action.surcharges_total'} />}
                                        />
                                    </BodyCell>
                                </Column>
                                <Column xs={4} nogutter>
                                    <BodyCell fullSize>
                                        <Label
                                            textAlign={alignments.right}
                                            text={props.policySummary.formattedEstimatedSurcharges || '$0.00'}
                                        />
                                    </BodyCell>
                                </Column>
                            </Row>
                            <Row nogutter>
                                <Column xs={8} nogutter>
                                    <BodyCell firstCell fullSize>
                                        <Label
                                            textAlign={alignments.left}
                                            text={<FormattedMessage id={'Profile_Summary_Action.discount_total'} />}
                                        />
                                    </BodyCell>
                                </Column>
                                <Column xs={4} nogutter>
                                    <BodyCell fullSize>
                                        <Label
                                            textAlign={alignments.right}
                                            text={props.policySummary.formattedEstimatedDiscounts || '$0.00'}
                                        />
                                    </BodyCell>
                                </Column>
                            </Row>
                            <Row nogutter>
                                <Column xs={8} nogutter>
                                    <BodyCell firstCell fullSize>
                                        <Label
                                            textAlign={alignments.left}
                                            text={<FormattedMessage id={'Profile_Summary_Action.fees'} />}
                                        />
                                    </BodyCell>
                                </Column>
                                <Column xs={4} nogutter>
                                    <BodyCell fullSize>
                                        <Label
                                            textAlign={alignments.right}
                                            text={props.policySummary.formattedEstimatedFees}
                                        />
                                    </BodyCell>
                                </Column>
                            </Row>
                            <Row nogutter>
                                <Column xs={8} nogutter>
                                    <FooterCell firstCell fullSize hasTopBorder>
                                        <Label
                                            bold
                                            textAlign={alignments.left}
                                            text={<FormattedMessage id={'Profile_Summary_Action.subtotal'} />}
                                        />
                                    </FooterCell>
                                </Column>
                                <Column xs={4} nogutter>
                                    <FooterCell fullSize hasTopBorder>
                                        <Label
                                            bold
                                            textAlign={alignments.right}
                                            text={getFormattedCurrency(subTotalAmount, currency, culture)}
                                        />
                                    </FooterCell>
                                </Column>
                            </Row>
                            <Row nogutter>
                                <Column xs={8} nogutter>
                                    <BodyCell firstCell fullSize>
                                        <Label
                                            textAlign={alignments.left}
                                            text={<FormattedMessage id={'Profile_Summary_Action.taxes'} />}
                                        />
                                    </BodyCell>
                                </Column>
                                <Column xs={4} nogutter>
                                    <BodyCell fullSize>
                                        <Label
                                            textAlign={alignments.right}
                                            text={props.policySummary.formattedEstimatedTaxes}
                                        />
                                    </BodyCell>
                                </Column>
                            </Row>
                            <Row nogutter>
                                <Column xs={8} nogutter>
                                    <FooterCell firstCell fullSize hasTopBorder>
                                        <Label
                                            bold
                                            textAlign={alignments.left}
                                            text={<FormattedMessage id={'Profile_Summary_Action.total'} />}
                                        />
                                    </FooterCell>
                                </Column>
                                <Column xs={4} nogutter>
                                    <FooterCell fullSize hasTopBorder>
                                        <Label
                                            bold
                                            textAlign={alignments.right}
                                            text={getFormattedCurrency(totalAmount, currency, culture)}
                                        />
                                    </FooterCell>
                                </Column>
                            </Row>
                        </Grid>
                    </Container>
                )}
            </PolicyFactsContainer>
        </React.Fragment>
    );
};

export default ProfileSummaryAction;
